import React, { useEffect, useRef, useState, useCallback } from "react";
import tw from "twin.macro";
import styled from "styled-components";

// Minimal inline SVG icons so we don't depend on extra asset files.
const Svg = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" {...props} />
);
const FoodIcon = (props) => (
  <Svg {...props}>
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" />
  </Svg>
);
const MarketIcon = (props) => (
  <Svg {...props}>
    <path d="M3 9l1-5h16l1 5M3 9v11a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V9M3 9h18M9 20V12h6v8" />
  </Svg>
);
const CategoryIcon = (props) => (
  <Svg {...props}>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
  </Svg>
);

// Shared dropdown that shows live search results for foods, markets and
// categories. Each result links into the marketplace (Foods tab) using the
// same cat=/market= query params the marketplace already filters on.
const Dropdown = styled.div`
  ${tw`absolute left-0 z-50 bg-white rounded-xl shadow-2xl border border-gray-200`}
  top: calc(100% + 0.5rem);
  width: 100%;
  min-width: 360px;
  max-width: 480px;
  max-height: 24rem;
  overflow-y: auto;
`;
const ResultRow = styled.button`
  ${tw`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-gray-100 last:border-b-0 transition-colors duration-150`}
  &:hover { background-color: #ecfdf5; }
`;
const ResultIcon = styled.span`
  ${tw`flex-shrink-0 rounded-lg flex items-center justify-center`}
  width: 36px;
  height: 36px;
  background-color: #064e3b;
  color: #fbbf24;
  svg { width: 18px; height: 18px; }
`;
const ResultText = tw.div`flex-1 min-w-0`;
const ResultTitle = tw.div`text-sm font-bold text-gray-800 truncate`;
const ResultSub = tw.div`text-xs text-gray-500 truncate`;
const TypeBadge = styled.span`
  ${tw`text-xs font-bold uppercase tracking-wider px-2 rounded-full flex-shrink-0`}
  font-size: 10px;
  padding-top: 2px;
  padding-bottom: 2px;
  background-color: #d1fae5;
  color: #047857;
`;
const EmptyState = tw.div`px-4 py-6 text-center text-sm text-gray-500`;
const LoadingRow = tw.div`px-4 py-3 text-center text-xs font-semibold text-gray-400`;
const SeeAll = styled.button`
  ${tw`w-full text-center px-4 py-3 text-sm font-bold transition-colors duration-150`}
  color: #047857;
  background-color: #ecfdf5;
  &:hover { background-color: #d1fae5; }
`;

const ICONS = {
  food: FoodIcon,
  market: MarketIcon,
  category: CategoryIcon,
};

const buildQuery = (item) => {
  if (item.type === "category") {
    const cat = item.queryBy || item.title || "";
    return `/marketplace?page=stock&tab=Foods&cat=${encodeURIComponent(cat)}`;
  }
  if (item.type === "market") {
    const marketValue =
      item.firestoreId || item.marketId || item.partnerMarketId || item.queryBy || item.title || "";
    return `/marketplace?page=stock&tab=Foods&market=${encodeURIComponent(marketValue)}`;
  }
  // food — filter the marketplace down to this specific item using a free-text
  // query so the user lands on the food they picked rather than its whole
  // category/market.
  const q = item.title || item.name || item.queryBy || "";
  const params = new URLSearchParams({ page: "stock", tab: "Foods" });
  if (q) params.set("q", q);
  return `/marketplace?${params.toString()}`;
};

const PAGE_SIZE = 8;

const SearchResultsDropdown = ({ results, query, onSelect }) => {
  const ref = useRef(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Reset paging whenever the query or the result set changes.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [query, results]);

  const handleScroll = useCallback(
    (e) => {
      const el = e.currentTarget;
      // Load the next page when the user scrolls near the bottom.
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
        setVisibleCount((c) => (c < results.length ? c + PAGE_SIZE : c));
      }
    },
    [results.length]
  );

  if (!query.trim()) return null;

  const visibleResults = results.slice(0, visibleCount);
  const hasMore = visibleCount < results.length;

  return (
    <Dropdown ref={ref} onScroll={handleScroll}>
      {results.length === 0 ? (
        <EmptyState>No matches for &ldquo;{query}&rdquo;</EmptyState>
      ) : (
        visibleResults.map((item) => {
          const Icon = ICONS[item.type] || FoodIcon;
          return (
            <ResultRow
              key={`${item.type}-${item.firestoreId || item.queryBy || item.title}-${item.title}`}
              onClick={() => onSelect(buildQuery(item), item)}
            >
              <ResultIcon>
                <Icon />
              </ResultIcon>
              <ResultText>
                <ResultTitle>{item.title}</ResultTitle>
                <ResultSub>{item.content || (Array.isArray(item.contentArray) ? item.contentArray.join(", ") : item.contentArray) || item.description || ""}</ResultSub>
              </ResultText>
              <TypeBadge>{item.type}</TypeBadge>
            </ResultRow>
          );
        })
      )}
      {hasMore && (
        <LoadingRow>Scroll for more results&hellip;</LoadingRow>
      )}
      <SeeAll onClick={() => onSelect(`/marketplace?page=stock&tab=Foods&q=${encodeURIComponent(query)}`)}>
        See all results for &ldquo;{query}&rdquo;
      </SeeAll>
    </Dropdown>
  );
};

export default SearchResultsDropdown;
